/**
 * App.tsx — Root component wiring auth, routing, and layout.
 *
 * Flow:
 * 1. AppMsalProvider wraps the tree with MSAL context.
 * 2. AuthGuard redirects unauthenticated users to EntraID login.
 * 3. AppShell provides the Layout (Header, Sidebar, EnvironmentSelector)
 *    with react-router-dom routing.
 * 4. Feature pages are rendered in the content area via <Outlet>.
 */

import { useState, useEffect } from 'react';
import { BrowserRouter, Routes, Route, Navigate, useNavigate, useLocation } from 'react-router-dom';
import { AppMsalProvider, AuthGuard, useAuth } from './auth';
import { getConfig } from './config';
import { ErrorBoundary } from './layout/ErrorBoundary';
import { createApiClient } from './api/client';
import type { ManagedEnvironment } from './api/types';
import { Layout } from './layout';
import {
  GuardrailsPage,
  DashboardsPage,
  AccountsPage,
  LogsPage,
} from './features';

function AppShell() {
  const config = getConfig();
  const { getAccessToken, user, logout } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();

  const [managedEnvironment, setManagedEnvironment] = useState<ManagedEnvironment>(
    (config.authorizedEnvironments[0] as ManagedEnvironment) ?? 'np',
  );

  const apiClient = createApiClient({
    getAccessToken,
    getManagedEnvironment: () => managedEnvironment,
    onUnauthenticated: () => {
      // Force re-login on 401 after silent renewal fails
      window.location.reload();
    },
  });

  // Emit login audit event once per browser session (persisted via sessionStorage)
  useEffect(() => {
    const key = 'irondome_login_audit_sent';
    if (!sessionStorage.getItem(key)) {
      sessionStorage.setItem(key, '1');
      apiClient.post('/auth/login').catch(() => {
        // Non-critical — do not block the UI if audit call fails
      });
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Simple role: derived after first API call to /me.
  // For initial render we assume admin (controls rendered; the BFF enforces).
  const role = 'admin' as const;

  return (
    <Layout
      managedEnvironment={managedEnvironment}
      authorizedEnvironments={config.authorizedEnvironments}
      onEnvironmentChange={(env) => setManagedEnvironment(env as ManagedEnvironment)}
      activePath={location.pathname}
      onNavigate={(path) => navigate(path)}
      breadcrumbs={[]}
      userName={user?.name}
      userEmail={user?.email}
      onLogout={logout}
    >
      <Routes>
        <Route path="/guardrails" element={<GuardrailsPage role={role} managedEnvironment={managedEnvironment} apiClient={apiClient} />} />
        <Route path="/dashboards" element={<DashboardsPage apiClient={apiClient} />} />
        <Route path="/accounts" element={<AccountsPage apiClient={apiClient} />} />
        <Route path="/logs" element={<LogsPage apiClient={apiClient} />} />
        <Route path="*" element={<Navigate to="/dashboards" replace />} />
      </Routes>
    </Layout>
  );
}

function App() {
  return (
    <BrowserRouter>
      <ErrorBoundary>
        <AppMsalProvider>
          <AuthGuard>
            <AppShell />
          </AuthGuard>
        </AppMsalProvider>
      </ErrorBoundary>
    </BrowserRouter>
  );
}

export default App;
